<template>
  <Teleport to="body">
    <Transition name="toast">
      <div
        v-if="toastStore.toasts.length"
        class="toaster__wrapper fixed bottom-10 right-0 md:right-10 z-[60] flex flex-col gap-2 w-full md:w-[40vw] px-6">
        <TransitionGroup name="toast" tag="ul">
          <li
            v-for="toast in toastStore.toasts"
            class="flex items-center gap-4 rounded-md"
            :class="[toastClassMap[toast.status]]"
            :key="toast.text">
            <div class="flex flex-col items-center w-full space-y-4 sm:items-end">
              <div
                class="w-full overflow-hidden rounded-lg shadow-lg pointer-events-auto ring-1 ring-black ring-opacity-5">
                <div class="p-4">
                  <div class="flex items-start">
                    <div
                      class="flex-shrink-0"
                      v-if="toast.status !== 'attention' && toast.status !== 'info'">
                      <svg
                        v-if="toast.status === 'success'"
                        class="w-6 aspect-w-1 aspect-h-1"
                        :class="toastIconMap[toast.status]"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10s10-4.5 10-10S17.5 2 12 2m-2 15l-5-5l1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9Z" />
                      </svg>
                      <svg
                        v-else-if="toast.status === 'warning'"
                        class="w-6 aspect-w-1 aspect-h-1"
                        :class="toastIconMap[toast.status]"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M13 13h-2V7h2m0 10h-2v-2h2M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2Z" />
                      </svg>
                      <svg
                        v-else
                        class="w-6 aspect-w-1 aspect-h-1"
                        :class="toastIconMap[toast.status]"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M12 2c5.53 0 10 4.47 10 10s-4.47 10-10 10S2 17.53 2 12S6.47 2 12 2m3.59 5L12 10.59L8.41 7L7 8.41L10.59 12L7 15.59L8.41 17L12 13.41L15.59 17L17 15.59L13.41 12L17 8.41L15.59 7Z" />
                      </svg>
                    </div>
                    <div class="ml-3 w-0 flex-1 pt-0.5">
                      <p
                        class="text-sm font-medium text-gray-900 capitalize"
                        v-if="!toast.hideTitle">
                        {{ $t(`common.toast.${toast.status}`) }}
                      </p>
                      <p class="mt-1 text-sm text-gray-500">
                        {{ toast.text }}
                      </p>
                    </div>
                    <div class="flex flex-shrink-0 ml-4">
                      <button
                        type="button"
                        class="inline-flex text-gray-400 rounded-md hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        @click="onClose(toast.id)">
                        <span class="sr-only">Close</span>
                        <svg
                          class="w-5 h-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true">
                          <path
                            d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </TransitionGroup>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { type TToastStatus, useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const toastClassMap: Record<TToastStatus, string> = {
  info: 'text-sky-500 bg-sky-300',
  attention: 'text-yellow-300 bg-yellow-300',
  warning: 'text-orange-500 bg-orange-100',
  error: 'text-red-500 bg-red-100',
  success: 'text-green-500 bg-green-100',
};

const toastIconMap: Record<TToastStatus, string> = {
  info: 'text-sky-300',
  attention: 'text-sky-500',
  warning: 'text-orange-500',
  error: 'text-orange-500',
  success: 'text-green-500',
};
const toastStore = useToastStore();

function onClose(id: number) {
  toastStore.closeToast(id);
}
</script>

<style scoped>
.toast-enter-from,
.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.toast-enter-active,
.toast-leave-active {
  transition: 0.25s ease all;
}
</style>
